import type { Channel } from "@sobidogam/platform-ports";
import type { ChannelBridge } from "./App";

export type ShareOutcome = "shared" | "copied" | "cancelled" | "failed";

/**
 * Text share for the shopping list and cards. Web uses the share sheet with a
 * public link; Apps in Toss uses the Toss share bridge with an intoss:// link
 * only (비게임 출시 가이드: 자사 웹 이동 유도 금지).
 */
export async function shareText(input: {
	text: string;
	webUrl: string;
	tossPath: string;
	channel: Channel;
	bridge?: ChannelBridge | undefined;
}): Promise<ShareOutcome> {
	if (input.channel === "ait") {
		if (input.bridge?.shareText)
			return (await input.bridge.shareText(input.text, input.tossPath)) ===
				"shared"
				? "shared"
				: "failed";
		return copy(input.text);
	}
	const message = `${input.text}\n${input.webUrl}`;
	const nav = navigator as Navigator & {
		share?: (data: ShareData) => Promise<void>;
	};
	if (nav.share) {
		try {
			await nav.share({ text: message });
			return "shared";
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError")
				return "cancelled";
		}
	}
	return copy(message);
}

async function copy(text: string): Promise<ShareOutcome> {
	try {
		await navigator.clipboard.writeText(text);
		return "copied";
	} catch {
		return "failed";
	}
}

export type CardShareOutcome =
	| "shared"
	| "link_shared"
	| "downloaded"
	| "cancelled"
	| "failed";

/**
 * #45 해금·리포트 카드 보내기. Web: files + link via the share sheet, link only
 * when files are not shareable, download as the last fallback. The card image
 * itself prints the address so a chat app that drops the text still carries
 * the way back. Toss: text + intoss:// link through the bridge only.
 */
export async function shareCard(input: {
	blob: Blob | null;
	filename: string;
	text: string;
	webUrl: string;
	tossPath: string;
	channel: Channel;
	bridge?: ChannelBridge | undefined;
	download: (blob: Blob, filename: string) => void;
}): Promise<CardShareOutcome> {
	if (input.channel === "ait") {
		if (input.bridge?.shareText) {
			const result = await input.bridge.shareText(input.text, input.tossPath);
			if (result === "shared") return "shared";
		}
		if (input.blob) {
			input.download(input.blob, input.filename);
			return "downloaded";
		}
		return "failed";
	}
	const nav = navigator as Navigator & {
		share?: (data: ShareData) => Promise<void>;
		canShare?: (data: ShareData) => boolean;
	};
	const isAbort = (error: unknown) =>
		error instanceof DOMException && error.name === "AbortError";
	if (input.blob && nav.share && nav.canShare) {
		const file = new File([input.blob], input.filename, { type: "image/png" });
		const data: ShareData = {
			files: [file],
			text: `${input.text}\n${input.webUrl}`,
		};
		if (nav.canShare(data)) {
			try {
				await nav.share(data);
				return "shared";
			} catch (error) {
				if (isAbort(error)) return "cancelled";
			}
		}
	}
	if (nav.share) {
		try {
			await nav.share({ text: input.text, url: input.webUrl });
			return "link_shared";
		} catch (error) {
			if (isAbort(error)) return "cancelled";
		}
	}
	if (input.blob) {
		input.download(input.blob, input.filename);
		return "downloaded";
	}
	return "failed";
}
